import React, { useState, useEffect, useRef } from 'react';
import { TourPlace } from '@/hooks/useTourPlaces';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
  type CarouselApi,
} from '@/components/ui/carousel';

interface TourPlacesGalleryProps {
  places: TourPlace[];
  loading: boolean;
}

const TourPlacesGallery: React.FC<TourPlacesGalleryProps> = ({ places, loading }) => {
  const [selectedPlace, setSelectedPlace] = useState<TourPlace | null>(null);
  const [carouselApi, setCarouselApi] = useState<CarouselApi>();
  const wheelTimeoutRef = useRef<NodeJS.Timeout>();

  useEffect(() => {
    if (!carouselApi) return;

    const container = carouselApi.rootNode();
    
    const handleWheel = (e: WheelEvent) => {
      e.preventDefault();
      
      clearTimeout(wheelTimeoutRef.current);
      
      const scrollAmount = e.deltaY || e.deltaX;
      
      if (Math.abs(scrollAmount) > 0) {
        carouselApi.scrollTo(carouselApi.selectedScrollSnap() + (scrollAmount > 0 ? 1 : -1));
      }
    };

    container.addEventListener('wheel', handleWheel, { passive: false });

    return () => {
      container.removeEventListener('wheel', handleWheel);
      clearTimeout(wheelTimeoutRef.current);
    };
  }, [carouselApi]);

  if (loading) {
    return (
      <div className="space-y-3">
        <h3 className="text-lg font-semibold">Places on this tour</h3>
        <div className="flex gap-3 overflow-hidden">
          {[1, 2, 3].map((i) => (
            <div key={i} className="flex-shrink-0 w-72 space-y-2">
              <Skeleton className="w-full aspect-video rounded-lg" />
              <Skeleton className="h-4 w-3/4" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (places.length === 0) {
    return null;
  }

  return (
    <>
      <div className="space-y-4">
        <h3 className="text-lg font-semibold">Discover the places</h3>
        
        <Carousel
          setApi={setCarouselApi}
          opts={{
            align: "start",
            loop: false,
            dragFree: true,
            slidesToScroll: 1,
          }}
          className="w-full relative"
        >
          <CarouselContent className="-ml-4 transition-transform duration-300 ease-out">
            {places.map((place, index) => (
              <CarouselItem key={index} className="pl-4 basis-[85%] md:basis-[45%] lg:basis-[33%]">
                <button
                  onClick={() => setSelectedPlace(place)}
                  className="group relative overflow-hidden rounded-lg transition-transform hover:scale-[1.02] focus:outline-none focus:ring-2 focus:ring-primary w-full"
                  aria-label={`Voir les détails de ${place.name}`}
                >
                  <div className="aspect-[4/3] relative">
                    <img
                      src={place.image || '/placeholder.svg'}
                      alt={place.name}
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                  </div>
                  <div className="mt-3 text-sm font-medium text-foreground text-left px-1">
                    {place.name}
                  </div>
                </button>
              </CarouselItem>
            ))}
          </CarouselContent>
          <CarouselPrevious className="absolute left-2 top-[calc(50%-2rem)] z-10 bg-black/30 hover:bg-black/50 text-white border-none backdrop-blur-sm transition-all duration-200" />
          <CarouselNext className="absolute right-2 top-[calc(50%-2rem)] z-10 bg-black/30 hover:bg-black/50 text-white border-none backdrop-blur-sm transition-all duration-200" />
        </Carousel>
      </div>

      <Dialog open={!!selectedPlace} onOpenChange={() => setSelectedPlace(null)}>
        <DialogContent className="max-w-4xl p-0 overflow-hidden z-[250]">
          {selectedPlace && (
            <div className="relative">
              <img
                src={selectedPlace.image || '/placeholder.svg'}
                alt={selectedPlace.name}
                className="w-full h-auto max-h-[80vh] object-contain bg-black"
              />
              <div className="absolute bottom-4 left-4 bg-black/70 backdrop-blur-sm text-white px-4 py-2 rounded-lg">
                <p className="font-semibold">{selectedPlace.name}</p>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default TourPlacesGallery;
